import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ctcService from '../../services/ctcService';

export const fetchCTCs = createAsyncThunk(
  'ctc/fetchCTCs',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ctcService.getAll(page, limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch CTCs');
    }
  }
);

export const fetchCTCById = createAsyncThunk(
  'ctc/fetchCTCById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ctcService.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch CTC');
    }
  }
);

export const createCTC = createAsyncThunk(
  'ctc/createCTC',
  async (ctcData, { rejectWithValue }) => {
    try {
      const response = await ctcService.create(ctcData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create CTC');
    }
  }
);

export const updateCTC = createAsyncThunk(
  'ctc/updateCTC',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await ctcService.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update CTC');
    }
  }
);

export const deleteCTC = createAsyncThunk(
  'ctc/deleteCTC',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ctcService.delete(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete CTC');
    }
  }
);

const initialState = {
  list: [],
  current: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  loading: false,
  error: null
};

const ctcSlice = createSlice({
  name: 'ctc',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCTCs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCTCs.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(fetchCTCs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCTCById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCTCById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchCTCById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCTC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCTC.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
      })
      .addCase(createCTC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCTC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCTC.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(ctc => ctc.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateCTC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCTC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCTC.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(ctc => ctc.id !== action.meta.arg);
      })
      .addCase(deleteCTC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetError } = ctcSlice.actions;
export default ctcSlice.reducer;
